<template>
    <div class="games-filter games-filter--mobile">
        <div class="games-filter__option">
            <DsButton
                type="interaction"
                size="sm"
                theme="dark"
                icon-left="filter"
                aria-label="Otevřít filtr"
                @click="openFilter"
            ></DsButton>
            <div class="games-filter__option-number games-filter__option-number--mobile" v-if="activeFilterCount">
                {{ activeFilterCount }}
            </div>
        </div>

        <div
            class="games-filter__option games-filter__option--sort"
            :class="{ 'games-filter__option--open': isSortOpen }"
        >
            <DsButton
                type="interaction"
                size="sm"
                theme="dark"
                icon-left="sort-by"
                aria-label="Seřadit hry"
                @click="toggleSort"
            ></DsButton>

            <div v-if="isSortOpen" class="games-filter__option-dialog games-filter__option-dialog--sort">
                <GamesSortOptions @sort-applied="closeSort" />
            </div>
        </div>

        <div
            v-show="isFilterOpen"
            id="games-filter-bottom-sheet"
            ref="bottomSheetRef"
            class="games-filter__popup-wrapper"
        >
            <div class="games-filter__popup">
                <DsButton
                    type="interaction"
                    size="sm"
                    theme="dark"
                    icon-left="cross"
                    aria-label="Zavřít filtr"
                    @click="closeFilter"
                    class="games-filter__popup-close"
                ></DsButton>
                <template v-if="filterOptions">
                    <h3 class="games-filter__popup__title">Žánr</h3>

                    <GamesFilterCategoryOptions
                        class="games-filter__popup-options"
                        :options="filterOptions.genres"
                        v-model="temporaryFilterParameters.genres"
                        :name="GamesFilterType.Genres"
                        @filter-changed="(item: GamesFilterCategory) => updateFilter(GamesFilterType.Genres, item)"
                    />
                    <h3 class="games-filter__popup__title">Výrobce</h3>
                    <GamesFilterCategoryOptions
                        class="games-filter__popup-options"
                        :options="filterOptions.manufacturers"
                        v-model="temporaryFilterParameters.manufacturers"
                        :name="GamesFilterType.Manufacturers"
                        @filter-changed="(item: GamesFilterCategory) => updateFilter(GamesFilterType.Manufacturers, item)"
                    />
                    <h3 class="games-filter__popup__title">Počet válců</h3>
                    <GamesFilterCategoryOptions
                        class="games-filter__popup-options"
                        :options="filterOptions.reels"
                        v-model="temporaryFilterParameters.reels"
                        :name="GamesFilterType.Reels"
                        @filter-changed="(item: GamesFilterCategory) => updateFilter(GamesFilterType.Reels, item)"
                    />
                    <h3 class="games-filter__popup__title">Počet linií</h3>
                    <GamesFilterCategoryOptions
                        class="games-filter__popup-options"
                        :options="filterOptions.lines"
                        v-model="temporaryFilterParameters.lines"
                        :name="GamesFilterType.Lines"
                        @filter-changed="(item: GamesFilterCategory) => updateFilter(GamesFilterType.Lines, item)"
                    />
                </template>
            </div>
        </div>
        <div v-show="isFilterOpen && !isFilterClosing" class="games-filter__popup-footer">
            <button class="games-button" :disabled="!filteredGamesCount" @click="clickApplyFilter">
                <span class="games-button__inner">Zobrazit {{ filterButtonLabel }}</span>
            </button>
            <DsButton
                class="games-filter__popup-footer-reset-button"
                size="lg"
                type="text"
                theme="dark"
                @click="clickResetFilter"
                >Resetovat filtr</DsButton
            >
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useGamesGrid } from "@/games/composables/use-games-grid";
import GamesFilterCategoryOptions from "./games-filter-category-options.vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import GamesSortOptions from "./games-sort-options.vue";
import { computed, nextTick, ref, watch } from "vue";
import { GamesFilterParameters } from "@/games/models/games-filter-parameters";
import { useBottomSheet } from "@/lotteries/category-page/composables/use-bottom-sheet";
import { GamesFilterType } from "@/games/models/games-filter-type";
import { GamesAnalytics } from "@/games/analytics/analytics-games";
import { GamesFilterCategory } from "@/games/models/games-filter-options";

const { reloadWithFilter, getFilteredGamesCount, filterParameters, filterOptions, totalCount } = useGamesGrid();

const bottomSheet = useBottomSheet("#games-filter-bottom-sheet", {
    keepOpenTolerance: 20,
    speedToForceClose: 3000,
    headerHeight: 64,
    safeBottomHeight: 400,
});
const bottomSheetRef = ref<HTMLElement | null>(null);

const isFilterOpen = ref(false);
const isSortOpen = ref(false);
const activeFilterCount = computed(() => {
    let filteredCount = 0;
    if (filterParameters.value.manufacturers.length) filteredCount += filterParameters.value.manufacturers.length;
    if (filterParameters.value.genres.length) filteredCount += filterParameters.value.genres.length;
    if (filterParameters.value.reels.length) filteredCount += filterParameters.value.reels.length;
    if (filterParameters.value.lines.length) filteredCount += filterParameters.value.lines.length;
    return filteredCount;
});
const filteredGamesCount = ref<number>(totalCount.value);
const filterButtonLabel = computed(() => {
    switch (filteredGamesCount.value) {
        case 1:
            return "1 hru";
        case 2:
        case 3:
        case 4:
            return `${filteredGamesCount.value} hry`;
        default:
            return `${filteredGamesCount.value} her`;
    }
});

const getInitialFilterParams = () => ({
    genres: [],
    manufacturers: [],
    lines: [],
    reels: [],
    getCountOnly: false,
    sortType: filterParameters.value.sortType,
    pageSize: filterParameters.value.pageSize,
    page: 1,
});
const temporaryFilterParameters = ref<GamesFilterParameters>(getInitialFilterParams());

const toggleSort = () => {
    isSortOpen.value = !isSortOpen.value;
};

const closeSort = () => {
    isSortOpen.value = false;
};

const openFilter = () => {
    closeSort();
    temporaryFilterParameters.value = { ...filterParameters.value };
    temporaryFilterParameters.value.getCountOnly = true;
    filteredGamesCount.value = totalCount.value;
    isFilterOpen.value = true;
};

const isFilterClosing = ref(false);
const closeFilter = async () => {
    isFilterClosing.value = true;
    await bottomSheet.hideAndDestroy();

    isFilterOpen.value = false;
    isFilterClosing.value = false;
    temporaryFilterParameters.value = getInitialFilterParams();
    GamesAnalytics.pushMobileFilterClosed();
};

const clickApplyFilter = async () => {
    filterParameters.value.genres = temporaryFilterParameters.value.genres;
    filterParameters.value.manufacturers = temporaryFilterParameters.value.manufacturers;
    filterParameters.value.lines = temporaryFilterParameters.value.lines;
    filterParameters.value.reels = temporaryFilterParameters.value.reels;
    await reloadWithFilter();
    closeFilter();
};

const updateFilter = async (type: GamesFilterType, item: GamesFilterCategory) => {
    const allAppliedFilterNames = GamesAnalytics.getFilterOptionNames(
        temporaryFilterParameters.value.genres,
        temporaryFilterParameters.value.manufacturers,
        temporaryFilterParameters.value.reels,
        temporaryFilterParameters.value.lines,
        filterOptions.value
    );

    if (temporaryFilterParameters.value[type].includes(item.guid)) {
        GamesAnalytics.pushFilterApplied(type, item.name, allAppliedFilterNames);
    } else {
        GamesAnalytics.pushFilterRemoved(type, [item.name], allAppliedFilterNames);
    }

    filteredGamesCount.value = await getFilteredGamesCount(temporaryFilterParameters.value);
};

const clickResetFilter = async () => {
    const filtersToRemove = GamesAnalytics.getFilterOptionNames(
        temporaryFilterParameters.value.genres,
        temporaryFilterParameters.value.manufacturers,
        temporaryFilterParameters.value.reels,
        temporaryFilterParameters.value.lines,
        filterOptions.value
    );
    GamesAnalytics.pushFilterRemoved(null, filtersToRemove, []);
    temporaryFilterParameters.value = getInitialFilterParams();
    filteredGamesCount.value = await getFilteredGamesCount(temporaryFilterParameters.value);
};

bottomSheet.onSheetClosed(() => {
    closeFilter();
});

watch(isFilterOpen, (isOpen) => {
    if (isOpen) {
        nextTick(() => {
            bottomSheet.create(bottomSheetRef.value.clientHeight);
        });
    }
});
</script>
